import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';

import { BudgetUtilizationBar } from '@/components/performance/budget-utilization-bar';
import { DataTable } from '@/components/glitch-ui/data-table';
import { metaBudgetPacingQueryOptions, metaIntegrationStatusQueryOptions } from '@/lib/query-options';
import { formatMoney, toDisplayAmount } from '@/lib/performance/money';
import { platformsInScope, type PlatformScope } from '@/lib/performance/types';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import type { MetaBudgetPacingRow } from '@/lib/types';

/**
 * Consolidated Budget tab (#5971, CP-10). The utilization bar is already
 * platform-keyed (one bar per platform), so it's reused directly. When Meta is
 * in scope, the per-entity Meta budget pacing detail is shown beneath it.
 */
export function BudgetPanel({ scope }: { scope: PlatformScope }) {
  const inScope = platformsInScope(scope);
  const metaInScope = inScope.includes('meta');
  // #6278 — Meta money renders in the Meta AD ACCOUNT's currency (#6182 sourcing),
  // not the org/Google default this panel previously used.
  const { data: metaStatus } = useQuery({ ...metaIntegrationStatusQueryOptions(), enabled: metaInScope });
  const { symbol } = useCurrencySymbol(undefined, metaStatus?.selected_ad_account?.currency);
  const cs = symbol ?? '';

  const { data: pacingResp } = useQuery(metaBudgetPacingQueryOptions(metaInScope));

  const columns = useMemo<ColumnDef<MetaBudgetPacingRow, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Entity',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.name || row.original.meta_id}</span>,
      },
      { accessorKey: 'entity_level', header: 'Level' },
      {
        accessorKey: 'daily_budget_cents',
        header: 'Daily budget',
        cell: ({ row }) => formatMoney(toDisplayAmount(row.original.daily_budget_cents, 'meta'), cs),
      },
      {
        accessorKey: 'spend_cents',
        header: 'Spend',
        cell: ({ row }) => formatMoney(toDisplayAmount(row.original.spend_cents, 'meta'), cs),
      },
      {
        accessorKey: 'pacing_pct',
        header: 'Pacing',
        cell: ({ row }) => (row.original.pacing_pct != null ? `${row.original.pacing_pct.toFixed(0)}%` : '—'),
      },
    ],
    [cs],
  );

  return (
    <div className="space-y-6">
      <BudgetUtilizationBar />
      {metaInScope && (
        <div>
          <p className="mb-2 text-sm font-medium text-gray-900">Meta budget pacing</p>
          {/* #6278 — pacing is computed against the snapshot sync's trailing 7-day window. */}
          <p className="mb-2 text-xs text-gray-500">Figures cover the last 7 days of synced data.</p>
          <DataTable
            data={pacingResp?.rows ?? []}
            columns={columns}
            pageSize={10}
            emptyState="No Meta budget data in the synced window"
          />
        </div>
      )}
    </div>
  );
}
